import * as React from "react";
import { useCallback, useState } from "react";
import { Button, Dialog, DialogFooter, DialogType, PrimaryButton } from "office-ui-fabric-react";
import { ApprovalDecisionType } from "../../store/ApprovalCenterStore";
import TextInput from "../common/inputs/TextInput/TextInput";
import LabellableComponent from "../common/LabellableComponent";
import { DecisionPopupProps } from "./approvalTabBase";
import { optionalCommentValidator, requiredCommentValidator } from "./common";

export interface GenericCommentPopupContent {
    approve?: { title: string; subText: string };
    reject?: { title: string; subText: string };
}

interface Props<TDetails> extends DecisionPopupProps<TDetails> {
    content?: GenericCommentPopupContent;
}

function GenericCommentPopup<TDetails>(props: Readonly<Props<TDetails>>) {
    const { initialDirection, onSubmit, onCancel, content } = props;
    const isApprove = initialDirection === ApprovalDecisionType.Approved;

    const [comment, setComment] = useState<string | undefined>();

    const validator = isApprove ? optionalCommentValidator : requiredCommentValidator;
    const isValid = validator.isValid(comment);

    const handleSubmit = useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit(comment ?? "");
    }, [isValid, comment, onSubmit]);

    const titleDefault = isApprove ? "Approve Request" : "Reject Request";
    const subTextDefault = isApprove
        ? "Are you sure you want to approve the request? Provide the comment below for the submitter if necessary."
        : "Are you sure you want to reject the request? Provide the comment below for the submitter.";

    const dialogContent = isApprove ? content?.approve : content?.reject;
    const commentLabel = isApprove ? "Approval Comment" : "Rejection Comment";

    return (
        <Dialog
            hidden={false}
            onDismiss={onCancel}
            maxWidth={500}
            dialogContentProps={{
                type: DialogType.close,
                title: dialogContent?.title ?? titleDefault,
                subText: dialogContent?.subText ?? subTextDefault
            }}
            modalProps={{
                className: "approval-decision-popup"
            }}>
            <div className="panel-area approval-decision-popup">
                <div className="grid-item">
                    <LabellableComponent className="field-container" label={commentLabel} required={!isApprove}>
                        <TextInput
                            value={comment ?? ""}
                            onChanged={val => setComment(val)}
                            validator={validator}
                            inputProps={{
                                multiline: true,
                                required: !isApprove
                            }}
                        />
                    </LabellableComponent>
                </div>
            </div>
            <DialogFooter>
                <PrimaryButton
                    onClick={handleSubmit}
                    text={isApprove ? "Approve" : "Reject"}
                    disabled={!isValid}
                    className={isApprove ? undefined : "button-danger"}
                />
                <Button onClick={onCancel} text="Cancel" />
            </DialogFooter>
        </Dialog>
    );
}

export default GenericCommentPopup;
